// src/calculators/chl55.js — декларацията по чл. 55, ал. 1 ЗДДФЛ и чл. 201,
// ал. 1 ЗКПО (задача 5/6). Сметката е в services/chl55.js; тук е екранът:
// таблица от редове по вид данък, тримесечие/година, платец/получател,
// СИДДО справочник (само ориентир — ставката се въвежда РЪЧНО) и печат на
// образец 4001. Цитатите са дословни от lex.bg (СПРАВКА-ЗКПО-СИДДО.md).

import { el, clear, field, input } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { dateInput } from '../shared/dates.js';
import { withLaw, hintify } from './law.js';
import { takeChl55Prefill } from './prefill.js';
import { personPicker } from './personPick.js';
import { state } from '../shared/state.js';
import { chl55Declaration, siddoThresholdCheck, ACTIVITIES } from '../../services/chl55.js';
import { CHL55_MONTHS } from '../../services/blanka.js';
// JSON-ът минава през services/siddo.js (CJS) — Vite и чистият Node ESM
// четат JSON различно, а CJS require работи и в двата. Default import,
// не именуван: лексерът на Node не разпознава named exports тук.
import siddoRef from '../../services/siddo.js';
const siddoCountries = siddoRef.countries;
const siddoMeta = siddoRef.meta;

const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });
const warnRow = (t) => el('div', { class: 'sev-warn', text: t });

const MONTH_LABEL = { 3: 'март', 6: 'юни', 9: 'септември', 12: 'декември' };

// Кратките имена за падащия списък на реда (пълните са в KIND_LABELS).
const KIND_SHORT = [
  ['rent44', 'Наем — авансов (чл. 44)'],
  ['other43', 'Свободна професия, граждански договор и др. — авансов (чл. 43)'],
  ['money44a', 'Доход по чл. 35 — авансов (чл. 44а)'],
  ['dividend38', 'Дивидент — 5 % (чл. 38, ал. 1)'],
  ['liquidation38', 'Ликвидационен дял — 5 %'],
  ['foreign37', 'Чуждестранно ФЛ — 10 % (чл. 37)'],
  ['condo38_12', 'Етажна собственост — 10 % (чл. 38, ал. 12)'],
  ['prize38', 'Награди / застраховки (чл. 38)'],
  ['zkpo194', 'ЗКПО: дивидент към ЮЛ — 5 % (чл. 194)'],
  ['zkpo195', 'ЗКПО: доход на чуждестранно ЮЛ — 10 % (чл. 195)'],
];

export function buildChl55Calc() {
  const now = new Date();
  // „Наем" подава тримесечие/година през prefill.js — взема се еднократно.
  const pre = takeChl55Prefill() || {};
  const f = {
    quarter: el('select', {}, ...[1, 2, 3, 4].map((q) => el('option', { value: q, text: q + '-во тримесечие', selected: q === (pre.quarter || Math.floor(now.getMonth() / 3) + 1) }))),
    year: el('select', {}, ...[2025, 2026, 2027].map((y) => el('option', { value: y, text: String(y), selected: y === (pre.year || now.getFullYear()) }))),
    role: el('select', {},
      el('option', { value: 'payer', text: 'Платец на дохода (предприятие / СОЛ)', selected: true }),
      el('option', { value: 'recipient', text: 'Получател на дохода (чл. 55, ал. 2; чл. 201, ал. 3)' })),
  };

  // „Граждански договор" подава и готов ред (чл. 43 с осигуровките) — иначе
  // се започва с празен ред за наем.
  let rows = [pre.row ? Object.assign({}, pre.row) : { kind: 'rent44', amount: '' }];
  const rowsBody = el('tbody');
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  const siddoBox = el('div', { style: 'margin-top:10px' });

  // ЛИЦЕТО (получателят на дохода) — забележка на Макс (26.08.2026):
  // декларацията се прави ЗА някого и той е досието. Изборът е от
  // досиетата на активната фирма; „+ ново лице" пише името на място.
  // Влиза в резултата (и в снимката за досието) и в печатния образец.
  // Самият избор е в ./personPick.js — общ с „Граждански договор".
  const person = personPicker(() => recompute());
  const personSel = person.sel;
  const personNameField = person.nameField;
  const personName = person.name;
  const ensurePersonId = person.ensureId;
  const loadPersons = person.load;

  // ---- Редакторът на редове ----
  function extrasFor(r, idx) {
    const wrap = el('div', { style: 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-top:4px' });
    const no = 'ред ' + (idx + 1);
    if (r.kind === 'dividend38' || r.kind === 'liquidation38' || r.kind === 'zkpo194') {
      const dd = dateInput(r.decisionDate || '');
      dd.setAttribute('aria-label', 'Дата на решението за разпределяне, ' + no);
      dd.title = 'Дата на РЕШЕНИЕТО — срокът тече от него';
      dd.style.maxWidth = '110px';
      dd.addEventListener('input', () => { r.decisionDate = dd.dataset.iso; recompute(); });
      wrap.appendChild(el('span', { style: 'font-size:12px;color:var(--muted)', text: 'Решение:' }));
      wrap.appendChild(dd);
    }
    if (r.kind === 'zkpo194') {
      const rec = el('select', { 'aria-label': 'Получател на дивидента, ' + no },
        el('option', { value: 'foreign', text: 'чуждестранно ЮЛ', selected: r.recipient === 'foreign' || !r.recipient }),
        el('option', { value: 'local_nontrader', text: 'местно ЮЛ-нетърговец / община', selected: r.recipient === 'local_nontrader' }),
        el('option', { value: 'eu_eea', text: 'ЮЛ от ЕС/ЕИП (чл. 194, ал. 3, т. 3)', selected: r.recipient === 'eu_eea' }),
        el('option', { value: 'local_trader', text: 'местно ЮЛ-ТЪРГОВЕЦ (извън чл. 194)', selected: r.recipient === 'local_trader' }));
      rec.addEventListener('change', () => { r.recipient = rec.value; recompute(); });
      wrap.appendChild(rec);
    }
    if (r.kind === 'prize38') {
      const cb = el('input', { type: 'checkbox', checked: !!r.life15, 'aria-label': 'Застраховка „Живот" 15 и повече години, ' + no });
      cb.addEventListener('change', () => { r.life15 = cb.checked; recompute(); });
      wrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:5px;font-size:12px' }, cb, el('span', { text: '„Живот" 15+ г. (7 %)' })));
    }
    if (r.kind === 'other43') {
      // Човек, който не е счетоводител, избира с думи какво работи — процентът
      // НПР по чл. 29, ал. 1 идва от избора; осигуровките са по избор.
      const act = el('select', { 'aria-label': 'Вид дейност, ' + no },
        ...ACTIVITIES.map((a) => el('option', { value: a.key, text: a.label + ' — ' + a.rate + ' %', selected: (r.activity || 'free25') === a.key })));
      act.style.maxWidth = '380px'; // най-дългото име иначе разтяга таблицата
      act.addEventListener('change', () => { r.activity = act.value; recompute(); });
      const hint = f.role.value === 'recipient' ? 'осигуровките на лицето за тримесечието' : 'удържаните от лицето осигуровки';
      const contrib = input({ type: 'number', step: '0.01', min: '0', value: r.contributions != null ? r.contributions : '', placeholder: hint, title: hint, 'aria-label': 'Осигуровки, ' + no });
      contrib.addEventListener('input', () => { r.contributions = contrib.value; recompute(); });
      wrap.appendChild(hintify(field('Вид дейност', act),
        'чл. 29, ал. 1 ЗДДФЛ', 'Придобитият доход се намалява с разходи за дейността — 25, 40 или 60 на сто според вида; не е нужно да ги доказвате.'));
      wrap.appendChild(hintify(field('Осигуровки', contrib, { size: 'm' }),
        'чл. 43, ал. 1 ЗДДФЛ', 'Авансовият данък е върху разликата между облагаемия доход и осигурителните вноски за месеците на тримесечието.'));
    }
    // IV тримесечие, платец: данък по чл. 43/44/44а се удържа само при писмено
    // желание на лицето (чл. 65, ал. 13) — иначе отметката няма смисъл.
    if ((r.kind === 'rent44' || r.kind === 'other43' || r.kind === 'money44a')
        && Number(f.quarter.value) === 4 && f.role.value === 'payer') {
      const q4 = el('input', { type: 'checkbox', checked: !!r.wantsQ4, 'aria-label': 'Лицето писмено е поискало удържане (чл. 65, ал. 13), ' + no });
      q4.addEventListener('change', () => { r.wantsQ4 = q4.checked; recompute(); });
      wrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:5px;font-size:12px' }, q4, el('span', { text: 'Лицето писмено е поискало удържане (чл. 65, ал. 13)' })));
    }
    if (r.kind === 'zkpo195') {
      const country = el('select', { 'aria-label': 'Държава по СИДДО, ' + no },
        el('option', { value: '', text: '— държава (СИДДО, по избор) —' }),
        ...siddoCountries.map((c) => el('option', { value: c.country, text: c.country, selected: r.siddoCountry === c.country })));
      country.addEventListener('change', () => { r.siddoCountry = country.value; recompute(); });
      const rate = input({ type: 'number', step: '0.01', min: '0', max: '10', value: r.siddoRate != null ? r.siddoRate : '', placeholder: 'СИДДО %', 'aria-label': 'Ръчна ставка по СИДДО, ' + no });
      rate.style.maxWidth = '90px';
      rate.addEventListener('input', () => { r.siddoRate = rate.value === '' ? null : Number(rate.value); recompute(); });
      const ex = el('input', { type: 'checkbox', checked: !!r.euExempt, 'aria-label': 'Освободено по чл. 195, ал. 7–12, ' + no });
      ex.addEventListener('change', () => { r.euExempt = ex.checked; recompute(); });
      const br = el('input', { type: 'checkbox', checked: !!r.exemptBroken, 'aria-label': 'Прекъснато двегодишно участие, ' + no });
      br.addEventListener('change', () => { r.exemptBroken = br.checked; recompute(); });
      const av = el('input', { type: 'checkbox', checked: !!r.isAdvance, 'aria-label': 'Авансово плащане, ' + no });
      av.addEventListener('change', () => { r.isAdvance = av.checked; recompute(); });
      wrap.appendChild(country);
      wrap.appendChild(rate);
      wrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:5px;font-size:12px' }, ex, el('span', { text: 'ЕС освобождаване (ал. 7–12)' })));
      wrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:5px;font-size:12px' }, br, el('span', { text: 'прекъснато участие (ал. 9)' })));
      wrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:5px;font-size:12px' }, av, el('span', { text: 'авансово плащане (ал. 5)' })));
    }
    return wrap;
  }

  function drawRows() {
    clear(rowsBody);
    rows.forEach((r, idx) => {
      const no = 'ред ' + (idx + 1);
      const kindSel = el('select', { 'aria-label': 'Вид данък, ' + no },
        ...KIND_SHORT.map(([v, t]) => el('option', { value: v, text: t, selected: r.kind === v })));
      kindSel.addEventListener('change', () => { r.kind = kindSel.value; drawRows(); recompute(); });
      // Въвежда се винаги брутното — НПР се смятат сами (ПРОМТ-15).
      const gross = r.kind === 'rent44' || r.kind === 'other43';
      const amount = input({ type: 'number', step: '0.01', min: '0', value: r.amount, placeholder: gross ? 'брутна сума' : 'сума', 'aria-label': 'Сума, ' + no });
      amount.addEventListener('input', () => { r.amount = amount.value; recompute(); });
      rowsBody.appendChild(el('tr', {},
        el('td', {}, kindSel, extrasFor(r, idx)),
        el('td', { class: 'num', style: 'width:140px;vertical-align:top' }, amount),
        el('td', { style: 'width:30px;vertical-align:top' },
          el('button', { class: 'btn sm danger', text: '×', 'aria-label': 'Изтрий ' + no, onClick: () => { rows.splice(idx, 1); if (!rows.length) rows.push({ kind: 'rent44', amount: '' }); drawRows(); recompute(); } }))));
    });
  }

  // ---- СИДДО справочникът (ориентир) + прагът по чл. 142 ДОПК ----
  const ytdInput = input({ type: 'number', step: '0.01', min: '0', placeholder: 'общ ГОДИШЕН доход на лицето' });
  const thresholdOut = el('div', {});
  ytdInput.addEventListener('input', () => drawThreshold());
  function drawThreshold() {
    clear(thresholdOut);
    const v = Number(ytdInput.value);
    if (!(v > 0)) return;
    const year = Number(f.year.value);
    const chk = siddoThresholdCheck(v, year >= 2026 ? 'EUR' : 'BGN');
    thresholdOut.appendChild((chk.simplified ? noteRow : warnRow)((chk.simplified ? '' : '⚠ ') + chk.note));
  }
  function drawSiddo() {
    clear(siddoBox);
    if (!rows.some((r) => r.kind === 'zkpo195')) return;
    siddoBox.appendChild(el('h4', { text: 'СИДДО — справочник (само ориентир)', style: 'margin:8px 0 4px' }));
    const chosen = rows.filter((r) => r.kind === 'zkpo195' && r.siddoCountry).map((r) => r.siddoCountry);
    [...new Set(chosen)].forEach((name) => {
      const c = siddoCountries.find((x) => x.country === name);
      if (!c) return;
      const hasData = !!(c.dividends.raw || c.interest.raw || c.royalties.raw);
      siddoBox.appendChild(hasData
        ? noteRow(name + ': дивиденти ' + c.dividends.raw + ' % · лихви ' + c.interest.raw + ' % · роялти ' + c.royalties.raw + ' % (ориентир, ' + c.source + ')')
        : warnRow('⚠ ' + name + ': няма данни — прочетете спогодбата и синтезирания текст по MLI (nra.bg).'));
    });
    siddoBox.appendChild(noteRow(siddoMeta.note));
    siddoBox.appendChild(noteRow('Напомняне: удостоверение за местно лице + деклариране на притежател на дохода (чл. 136 и чл. 136а ДОПК).'));
    siddoBox.appendChild(field('Проверка на прага по чл. 142 ДОПК (годишен доход на лицето)', ytdInput, { size: 'm' }));
    siddoBox.appendChild(thresholdOut);
  }

  // ---- Резултатът ----
  let lastDecl = null;
  function recompute() {
    clear(result); clear(notesBox);
    drawSiddo();
    const filled = rows.filter((r) => Number(r.amount) > 0).map((r) => Object.assign({}, r, { amount: Number(r.amount) }));
    if (!filled.length) {
      lastDecl = null;
      result.appendChild(el('div', { class: 'calc-line', text: 'Добавете поне един ред със сума.' }));
      return;
    }
    const d = chl55Declaration({ quarter: Number(f.quarter.value), year: Number(f.year.value), role: f.role.value, rows: filled });
    lastDecl = d;
    const m = (x) => money(x, d.currency);
    // Лицето най-отгоре в резултата — така влиза и в снимката за досието.
    if (personName()) result.appendChild(line('Получател на дохода', personName()));
    d.rows.forEach((r) => {
      if (r.nprRate != null) {
        // Разбивката като в ръчната сметка на счетоводителя — всеки ред
        // отделно, така влиза и в снимката за досието.
        result.appendChild(line(r.label.replace(/ ЗДДФЛ, авансов 10 %/, '') + ' — доход', m(r.gross)));
        result.appendChild(line('− Нормативно признати разходи ' + r.nprRate + ' %', m(r.npr)));
        if (r.contributions > 0) result.appendChild(line('− Осигурителни вноски', m(r.contributions)));
        result.appendChild(line('= Данъчна основа', m(r.base)));
        result.appendChild(line('Авансов данък ' + r.rate + ' %', m(r.tax)));
      } else {
        result.appendChild(line(r.label + ' — основа ' + m(r.amount) + (r.excluded ? ' (не влиза)' : ''), r.excluded ? '—' : m(r.tax)));
      }
      r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
      if (r.dueDate && r.dueDate !== d.dueDate) {
        notesBox.appendChild(warnRow('⚠ Редът „' + r.label + '" има собствен срок: ' + r.dueDate.split('-').reverse().join('.') + ' (от решението).'));
      }
    });
    result.appendChild(line('Авансови данъци (чл. 43/44/44а ЗДДФЛ)', m(d.totals.advance)));
    result.appendChild(line('Окончателни данъци ЗДДФЛ (чл. 37/38)', m(d.totals.final)));
    result.appendChild(line('Данък при източника ЗКПО (чл. 194/195)', m(d.totals.zkpo)));
    result.appendChild(line('ОБЩО за декларацията', m(d.totals.total), true));
    result.appendChild(line('Срок за подаване и внасяне', d.dueDate.split('-').reverse().join('.')));
    d.notes.forEach((n) => notesBox.appendChild(/проверете при подаване/i.test(n) ? warnRow('⚠ ' + n) : noteRow(n)));
    // Връзка с Работната бланка: чл. 55 стои в март/юни/септември/декември.
    const bm = CHL55_MONTHS[Number(f.quarter.value) - 1];
    notesBox.appendChild(noteRow('В Работната бланка това тримесечие отговаря на клетката „чл. 55" за месец ' + (MONTH_LABEL[bm] || bm) + '.'));
  }

  // ---- Печат на образец 4001 ----
  async function printPdf() {
    if (!lastDecl) { toast('Първо попълнете поне един ред със сума.', true); return; }
    if (!state().activeCompany) { toast('Изберете активна фирма — тя влиза в заглавката на образеца.', true); return; }
    try {
      // Лицето пътува към образеца: при „получател" влиза в Част II,
      // иначе излиза като ред „Получател на дохода".
      const personId = await ensurePersonId();
      const res = await fetch('/api/chl55/pdf', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: state().activeCompany,
          person_id: personId,
          declaration: { quarter: Number(f.quarter.value), year: Number(f.year.value), role: f.role.value, rows: rows.filter((r) => Number(r.amount) > 0) },
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Грешка при печата.'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = el('a', { href: url, download: 'chl55-obr4001-' + f.year.value + '-Q' + f.quarter.value + '.pdf' });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast('Образец 4001 е свален. Подаването е по електронен път в НАП.');
    } catch (e) { toast(e.message, true); }
  }

  Object.values(f).forEach((n) => n.addEventListener('change', recompute));
  // Отметката за IV тримесечие и подсказката за осигуровките зависят от тях;
  // въведеното живее в rows, затова прерисуването нищо не губи.
  f.quarter.addEventListener('change', drawRows);
  f.role.addEventListener('change', drawRows);
  drawRows();
  loadPersons();

  const panel = el('div', { class: 'panel calc-panel', style: 'max-width:860px' },
    el('h3', { class: 'calc-title', text: 'Декларация по чл. 55 ЗДДФЛ / чл. 201 ЗКПО (обр. 4001)' }),
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' },
      hintify(field('Тримесечие', f.quarter, { size: 'm' }),
        'чл. 56, ал. 1 ЗДДФЛ; чл. 201, ал. 1 ЗКПО', 'Декларацията се подава в срока за внасяне — до края на месеца, следващ тримесечието.'),
      field('Година', f.year, { size: 's' }), field('Кой декларира', f.role)),
    el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end' },
      field('Получател на дохода (лице от „Досиета")', personSel),
      personNameField),
    el('h4', { text: 'Редове (доходи с дължим данък)', style: 'margin:10px 0 6px' }),
    el('table', { class: 'items' },
      el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'Вид данък' }), el('th', { scope: 'col', class: 'num', text: 'Сума' }), el('th'))),
      rowsBody),
    el('button', { class: 'btn sm ghost', type: 'button', text: '+ Нов ред', onClick: () => { rows.push({ kind: 'rent44', amount: '' }); drawRows(); recompute(); } }),
    siddoBox,
    result,
    notesBox,
    el('div', { style: 'margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;align-items:center' },
      el('button', { class: 'btn', type: 'button', text: 'Печат — образец 4001 (PDF)', onClick: printPdf }),
      el('span', { style: 'font-size:12px;color:var(--muted)', text: 'Помощен образец за архив и подпис — реалното подаване е по електронен път в НАП (чл. 56, ал. 2).' })));

  recompute();

  const wrapped = withLaw(panel, {
    when: [
      'Фирмата е удържала авансов данък за наем или хонорари — декларира тримесечно с обр. 4001.',
      'Свободна професия: въвеждате брутната сума от фактурите — 25 % разходи и осигуровките се приспадат сами.',
      'Взето е решение за дивидент: 5 % върху брутното по решението, срокът тече от тримесечието на РЕШЕНИЕТО.',
      'Плащане към чуждестранно ЮЛ (лихва, роялти, услуги) — данък при източника по чл. 195 ЗКПО; при СИДДО ставката се въвежда ръчно след удостоверяване.',
      'Физическо лице само внася данъка си (платецът не удържа) — подава същата декларация като получател.',
      'Периоди до 31.12.2025 г. се декларират в левове; от 01.01.2026 г. — в евро.',
    ],
    quotes: [
      { ref: 'чл. 55, ал. 1 ЗДДФЛ (изм. ДВ, бр. 94/2012)', text: 'Предприятията и самоосигуряващите се лица – платци на доходи, задължени да удържат и внасят данъци по реда на този закон, подават декларация по образец за дължимите данъци.' },
      { ref: 'чл. 56, ал. 1 и 2 ЗДДФЛ', text: 'Декларацията по чл. 55, ал. 1 се подава в сроковете за внасяне на дължимите данъци. … Предприятията – платци на доходи, както и самоосигуряващите се лица по смисъла на Кодекса за социално осигуряване, подават декларацията по чл. 55, ал. 1 само по електронен път.' },
      { ref: 'чл. 29, ал. 1, т. 3 ЗДДФЛ', text: 'Облагаемият доход от стопанска дейност на физическите лица, които не са търговци по смисъла на Търговския закон, се определя, като придобитият доход се намалява с разходи за дейността, както следва: … с 25 на сто за доходи от упражняване на свободна професия извън тази по т. 2, буква „г" или възнаграждения по извънтрудови правоотношения.' },
      { ref: 'чл. 43, ал. 1 и 3 ЗДДФЛ', text: 'Лице, придобило доход от стопанска дейност по чл. 29, дължи авансово данък върху разликата между облагаемия доход и вноските, които самоосигуряващото се лице е задължено да прави за своя сметка за месеците на тримесечието, през което е придобит облагаемият доход, а ако лицето не е самоосигуряващо се - между облагаемия доход и удържаните задължителни осигурителни вноски. … Размерът на дължимия данък се изчислява, като разликата по ал. 1 се умножи по данъчна ставка 10 на сто.' },
      { ref: 'чл. 67, ал. 2 ЗДДФЛ', text: 'Не се внася авансов данък по чл. 43, ал. 6 и чл. 44, ал. 3 за доходи, придобити през четвъртото тримесечие на данъчната година.' },
      { ref: 'чл. 65, ал. 2 ЗДДФЛ (изм. ДВ, бр. 94/2012)', text: 'Данъкът по чл. 46 за доходите по чл. 38, ал. 2 се удържа и внася от предприятието – платец на дохода, в срок до края на месеца, следващ тримесечието, през което е взето решението за разпределяне на дивидент.' },
      { ref: 'чл. 201, ал. 1 ЗКПО (изм. ДВ, бр. 94/2012)', text: 'Лицата, задължени да удържат и внасят данък при източника по чл. 194 и 195, декларират дължимия данък за тримесечието с декларация по образец в срок до края на месеца, следващ тримесечието.' },
      { ref: 'чл. 202, ал. 1 и 2 ЗКПО', text: 'Платците на доходи, удържащи данъка при източника по чл. 194, са длъжни да внесат дължимите данъци в срок до края на месеца, следващ тримесечието, през което е взето решението за разпределяне на дивиденти или на ликвидационни дялове. … Платците на доходи, удържащи данъка при източника по чл. 195, са длъжни да внесат дължимите данъци в срок до края на месеца, следващ тримесечието на начисляване на дохода.' },
      { ref: 'чл. 195, ал. 5 ЗКПО', text: 'Авансовите плащания във връзка с доходите по ал. 1 не подлежат на облагане с данък при източника.' },
      { ref: 'чл. 142, ал. 1 ДОПК (изм. ДВ, бр. 14/2011)', text: 'Когато платец начислява на чуждестранно лице доходи от източник в страната с общ размер до 500 000 лв. годишно, обстоятелствата по чл. 136 се удостоверяват пред платеца на дохода. В този случай искане по чл. 137, ал. 1 не се подава.' },
      { ref: 'чл. 202а, ал. 1 ЗКПО (бележка — не се смята тук)', text: 'Чуждестранно юридическо лице, което е местно лице за данъчни цели на държава – членка на Европейския съюз, или на друга държава – страна по Споразумението за Европейското икономическо пространство, има право да избере да преизчисли данъка при източника за доходите по чл. 12, ал. 2, 3, 5 и 8.' },
    ],
  });
  // Мостът към „Запази в досие" (по модела на _dividend): избраното в панела
  // лице казва къде да се запише БЕЗ диалог; ново име първо създава досие.
  wrapped._dossierPreset = async () => {
    const id = await ensurePersonId();
    return id ? { personId: id, name: personName() } : null;
  };
  return wrapped;
}
